import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DRAuraLogoFull } from "./LogoExports";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, isPro, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Theme & language state
  const [theme, setTheme] = useState(localStorage.getItem("draura_theme") || "dark");
  const [lang, setLang] = useState(localStorage.getItem("draura_lang") || "en");

  // Refs
  const themeRef = useRef(null);
  const langRef = useRef(null);
  const userRef = useRef(null);

  // Nav links
  const navLinks = [
    { to: "/", label: t("home") },
    { to: "/buy-ingredients", label: t("shop") },
    { to: "/community", label: t("community") },
    { to: "/meal-planner", label: t("mealPlanner") },
    { to: "/pricing", label: t("pricing") },
    { to: "/about", label: t("about") },
  ];

  // Language change helper
  const changeLang = (code) => {
    i18n.changeLanguage(code);
    setLang(code);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply theme
  useEffect(() => {
    document.documentElement.className = "";
    if (theme !== "dark") {
      document.documentElement.classList.add(`theme-${theme}`);
    }
    localStorage.setItem("draura_theme", theme);
  }, [theme]);

  // Apply language
  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("draura_lang", lang);
  }, [lang]);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  async function handleLogout() {
    try {
      await logout();
      toast.success(t("signOut"));
      navigate("/");
    } catch (err) {
      toast.error("Error signing out");
    }
  }

  return (
    <header className="navbar container">
      <Link to="/" className="logo">
        <DRAuraLogoFull />
      </Link>

      <nav className="nav-links hide-mobile">
        {navLinks.map((link) => (
          <Link key={link.to} to={link.to}>
            {link.label}
          </Link>
        ))}
      </nav>

      <div className="actions flex gap-md">
        {/* Theme Switcher */}
        <div className="dropdown" ref={themeRef}>
          <button className="dropdown-toggle" onClick={() => setThemeOpen(!themeOpen)}>
            🎨 Themes
          </button>
          {themeOpen && (
            <div className="dropdown-menu slide-down">
              <button onClick={() => setTheme("dark")}>🌙 Dark</button>
              <button onClick={() => setTheme("deeper")}>🔮 Luxury</button>
              <button onClick={() => setTheme("warm")}>🔥 Deep Space</button>
              <button onClick={() => setTheme("light")}>☀️ Light Mode</button>
            </div>
          )}
        </div>

        {/* Language Switcher */}
        <div className="dropdown" ref={langRef}>
          <button className="dropdown-toggle" onClick={() => setLangOpen(!langOpen)}>
            🌐 Language
          </button>
          {langOpen && (
            <div className="dropdown-menu slide-down">
              <button onClick={() => changeLang("en")}>🇬🇧 English</button>
              <button onClick={() => changeLang("hi")}>🇮🇳 हिंदी</button>
              <button onClick={() => changeLang("es")}>🇪🇸 Español</button>
              <button onClick={() => changeLang("fr")}>🇫🇷 Français</button>
              <button onClick={() => changeLang("de")}>🇩🇪 Deutsch</button>
              <button onClick={() => changeLang("pt")}>🇵🇹 Português</button>
              <button onClick={() => changeLang("ar")}>🇸🇦 العربية</button>
              <button onClick={() => changeLang("zh")}>🇨🇳 中文</button>
              <button onClick={() => changeLang("ja")}>🇯🇵 日本語</button>
              <button onClick={() => changeLang("ko")}>🇰🇷 한국어</button>
            </div>
          )}
        </div>

        {/* User Menu with Avatar */}
        {user ? (
          <div className="dropdown" ref={userRef}>
            <button
              className="dropdown-toggle avatar-btn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <img
                src={user.avatarUrl || "/default-avatar.png"}
                alt="avatar"
                className="avatar"
              />
            </button>
            {userMenuOpen && (
              <div className="dropdown-menu slide-down">
                <button onClick={() => navigate("/profile")}>{t("profile")}</button>
                <button onClick={handleLogout}>{t("signOut")}</button>
              </div>
            )}
          </div>
        ) : (
          <div className="auth-buttons">
            <Link to="/signin" className="btn btn-outline">
              Sign In
            </Link>
            {!isPro && (
              <Link to="/pricing" className="btn btn-gold hide-mobile">
                Get Pro
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Mobile menu toggle */}
      <button className="hamburger show-mobile" onClick={() => setMenuOpen(!menuOpen)}>
        ☰
      </button>

      {menuOpen && (
        <div className="mobile-menu">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to}>
              {link.label}
            </Link>
          ))}
          <div className="theme-row">
            <button onClick={() => setTheme("dark")}>🌙 Dark</button>
            <button onClick={() => setTheme("deeper")}>🔮 Luxury</button>
            <button onClick={() => setTheme("warm")}>🔥 Deep Space</button>
            <button onClick={() => setTheme("light")}>☀️ Light Mode</button>
          </div>
          <div className="lang-row">
            <button onClick={() => changeLang("en")}>🇬🇧 EN</button>
            <button onClick={() => changeLang("hi")}>🇮🇳 HI</button>
            <button onClick={() => changeLang("es")}>🇪🇸 ES</button>
            <button onClick={() => changeLang("fr")}>🇫🇷 FR</button>
          </div>
          {!isPro && (
            <Link to="/pricing" className="btn btn-gold">
              Upgrade to Pro
            </Link>
          )}
        </div>
      )}
    </header>
  );
}